import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useCart } from "../CartContext";
import { api, fmtINR } from "../api";
import { Crown, Truck, X, Heart, ShoppingBag, Stethoscope } from "lucide-react";

const CONSULT_STATUS_LABELS = { new: "Requested", contacted: "Contacted", scheduled: "Scheduled", closed: "Closed" };

export default function Account() {
    const { user, logout } = useAuth();
    const { add } = useCart();
    const [orders, setOrders] = useState([]);
    const [wishlist, setWishlist] = useState([]);
    const [trackOrder, setTrackOrder] = useState(null);
    const [consults, setConsults] = useState([]);

    const loadWishlist = useCallback(() => {
        api.get("/wishlist").then((r) => setWishlist(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (user) {
            api.get("/orders/me").then((r) => setOrders(r.data || []));
            api.get("/consultations/me").then((r) => setConsults(r.data || [])).catch(() => {});
            loadWishlist();
        }
    }, [user, loadWishlist]);

    const removeFromWishlist = async (productId) => {
        try {
            await api.delete(`/wishlist/${productId}`);
            setWishlist((w) => w.filter((p) => p.id !== productId));
        } catch (err) {
            console.warn("Wishlist remove failed:", err?.message || err);
        }
    };

    if (!user) {
        return (
            <div className="max-w-xl mx-auto px-5 py-24 text-center">
                <h1 className="font-serif text-3xl mb-3">Please sign in</h1>
                <Link to="/login" className="btn btn-primary">Sign in</Link>
            </div>
        );
    }

    return (
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12" data-testid="account-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <div className="eyebrow mb-2">My Account</div>
                    <h1 className="font-serif text-[34px] sm:text-[44px] leading-[1.05] flex items-center gap-3 flex-wrap">
                        Hi, {user.name?.split(" ")[0]}
                        {user.is_vip && (
                            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] bg-[var(--taupe)] text-white px-2.5 py-1 rounded-full" data-testid="vip-badge">
                                <Crown size={12} /> VIP
                            </span>
                        )}
                    </h1>
                    <p className="text-[var(--muted)] mt-2 break-all">{user.email}</p>
                    {user.is_vip && (
                        <p className="text-[13px] text-[var(--taupe)] mt-1">Every order is 100% complimentary — checkout bypasses payment.</p>
                    )}
                </div>
                <button onClick={logout} className="btn btn-outline" data-testid="account-logout">Sign out</button>
            </div>

            {/* Wishlist */}
            <section id="wishlist" className="mb-12">
                <h2 className="font-serif text-2xl mb-4 flex items-center gap-2">
                    <Heart size={20} className="text-[var(--terra)]" /> Saved Items
                </h2>
                {wishlist.length === 0 ? (
                    <div className="card-soft p-6 text-center text-[var(--muted)] text-[14px]" data-testid="wishlist-empty">
                        Nothing saved yet — tap the heart on any product to save it here.
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="wishlist-grid">
                        {wishlist.map((p) => (
                            <div key={p.id} className="card-soft p-4 flex flex-col" data-testid={`wishlist-item-${p.id}`}>
                                <Link to={`/product/${p.id}`} className="block aspect-square bg-gradient-to-b from-[#f5ede1] to-[#d8c4a8] rounded-lg overflow-hidden mb-3 grid place-items-center">
                                    {p.images?.[0] ? (
                                        <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="text-5xl">{p.emoji || "🌿"}</span>
                                    )}
                                </Link>
                                <div className="text-[13px] font-medium text-[var(--ink)] mb-1 line-clamp-2">{p.name}</div>
                                <div className="text-[12px] text-[var(--body)] mb-3">{fmtINR(p.price)}</div>
                                <div className="flex gap-2 mt-auto">
                                    <button onClick={() => add(p, 1)} className="btn btn-primary btn-sm flex-1" data-testid={`wishlist-add-to-cart-${p.id}`}>
                                        <ShoppingBag size={12} /> Add
                                    </button>
                                    <button onClick={() => removeFromWishlist(p.id)} className="btn btn-outline btn-sm" data-testid={`wishlist-remove-${p.id}`} aria-label="Remove">
                                        <X size={12} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <h2 className="font-serif text-2xl mb-4">Your Orders</h2>
            {orders.length === 0 ? (
                <div className="card-soft p-8 sm:p-12 text-center">
                    <div className="text-5xl mb-3">🌿</div>
                    <p className="text-[var(--muted)] mb-4">No orders yet. Time for a wellness ritual!</p>
                    <Link to="/shop" className="btn btn-primary">Shop now</Link>
                </div>
            ) : (
                <div className="space-y-4" data-testid="orders-list">
                    {orders.map((o) => (
                        <div key={o.id} className="card-soft p-4 sm:p-5" data-testid={`order-row-${o.id}`}>
                            <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Order</div>
                                    <div className="font-mono font-medium">{o.order_number}</div>
                                </div>
                                <span className={`pill pill-${o.status}`}>{o.status}</span>
                            </div>
                            <div className="flex flex-wrap gap-3 mb-3">
                                {o.items.map((it) => (
                                    <div key={it.product_id} className="flex items-center gap-2 text-sm">
                                        <span className="text-2xl">{it.emoji}</span>
                                        <span>{it.name} × {it.quantity}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="flex flex-wrap justify-between items-center gap-2 text-sm">
                                <span className="text-[var(--muted)]">
                                    {new Date(o.created_at).toLocaleDateString()} · {{ cod: "Cash on Delivery", gokwik: "GoKwik", vip_free: "VIP Free", razorpay: "Razorpay" }[o.payment_method] || o.payment_method}
                                </span>
                                <div className="flex items-center gap-3">
                                    <span className="font-semibold">{fmtINR(o.total)}</span>
                                    <button
                                        onClick={() => setTrackOrder(o)}
                                        className="btn btn-outline btn-sm"
                                        data-testid={`track-order-${o.id}`}
                                    >
                                        <Truck size={14} /> Details & Track
                                    </button>
                                </div>
                            </div>
                            {o.delhivery_waybill && (
                                <div className="text-[12px] text-[var(--muted)] mt-2">
                                    Waybill <span className="font-mono text-[var(--ink)]">{o.delhivery_waybill}</span>
                                    {o.delhivery_status && <> · {o.delhivery_status}</>}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Appointments */}
            <section id="appointments" className="mt-12">
                <h2 className="font-serif text-2xl mb-4 flex items-center gap-2">
                    <Stethoscope size={20} className="text-[var(--taupe)]" /> My Appointments
                </h2>
                {consults.length === 0 ? (
                    <div className="card-soft p-6 text-center text-[var(--muted)] text-[14px]" data-testid="appointments-empty">
                        No consultation requests yet.{" "}
                        <Link to="/consult-doctor" className="underline text-[var(--taupe)]">Book a doctor consultation</Link>
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="appointments-list">
                        {consults.map((c) => (
                            <div key={c.id} className="card-soft p-4 sm:p-5" data-testid={`appointment-row-${c.id}`}>
                                <div className="flex items-start justify-between flex-wrap gap-2">
                                    <div className="min-w-0">
                                        <div className="font-medium text-[var(--ink)] text-[15px]">{c.concern}</div>
                                        {c.note && <p className="text-[13px] text-[var(--body)] mt-1 line-clamp-2">{c.note}</p>}
                                        <div className="text-[12px] text-[var(--muted)] mt-2">
                                            Requested on {new Date(c.created_at).toLocaleDateString()} · {c.phone}
                                        </div>
                                    </div>
                                    <span className={`pill pill-consult-${c.status}`} data-testid={`appointment-status-${c.id}`}>
                                        {CONSULT_STATUS_LABELS[c.status] || c.status}
                                    </span>
                                </div>
                                {c.status === "new" && (
                                    <p className="text-[12px] text-[var(--muted)] mt-2">We'll email you within 24 hours with available slots.</p>
                                )}
                                {c.status === "scheduled" && (
                                    <p className="text-[12px] text-green-800 mt-2">Your consultation is scheduled — check your email for the appointment details.</p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {trackOrder && (
                <TrackingModal
                    order={trackOrder}
                    onClose={() => setTrackOrder(null)}
                    onCancelled={(id) => {
                        setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status: "cancelled" } : o)));
                        setTrackOrder((t) => (t && t.id === id ? { ...t, status: "cancelled" } : t));
                    }}
                />
            )}
        </div>
    );
}

function TrackingModal({ order, onClose, onCancelled }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [confirming, setConfirming] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [cancelMsg, setCancelMsg] = useState("");
    const [cancelErr, setCancelErr] = useState("");
    const status = (order.status || "").toLowerCase();
    const canCancel = status !== "delivered" && status !== "cancelled";

    const doCancel = async () => {
        setCancelling(true);
        setCancelErr("");
        try {
            await api.post(`/orders/${order.id}/cancel`);
            setCancelMsg("Your order has been cancelled successfully.");
            setConfirming(false);
            onCancelled?.(order.id);
        } catch (e) {
            setCancelErr(e?.response?.data?.detail || "Could not cancel the order. Please try again.");
        } finally {
            setCancelling(false);
        }
    };

    useEffect(() => {
        api.get(`/orders/${order.id}/tracking`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(e?.response?.data?.detail || e.message || "Tracking unavailable"));
    }, [order.id]);

    const addr = order.shipping_address || {};
    const addressLine = [addr.line1, addr.line2, addr.city, addr.state, addr.pincode].filter(Boolean).join(", ");
    const ship = ((data?.tracking?.ShipmentData || [])[0] || {}).Shipment || {};
    const latest = ship.Status || {};
    const scans = (ship.Scans || []).map((s) => s.ScanDetail || s).reverse();
    const payLabel = { cod: "Cash on Delivery", gokwik: "GoKwik", vip_free: "VIP Complimentary", razorpay: "Razorpay" }[order.payment_method] || order.payment_method;

    return (
        <div className="drawer-backdrop" onClick={onClose} data-testid="tracking-modal-backdrop">
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white max-w-[560px] mx-auto my-6 sm:my-10 rounded-2xl shadow-xl overflow-hidden"
                data-testid="tracking-modal"
            >
                <div className="flex items-center justify-between p-5 border-b border-[var(--line-soft)]">
                    <div>
                        <div className="overline">Order Details</div>
                        <div className="font-serif text-[22px] text-[var(--ink)]">{order.order_number}</div>
                    </div>
                    <button onClick={onClose} className="text-[var(--body)] hover:text-[var(--ink)] p-1" data-testid="close-tracking">
                        <X size={18} />
                    </button>
                </div>
                <div className="p-5 max-h-[70vh] overflow-y-auto">
                    {/* Order summary */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] mb-4">
                        <div><span className="text-[var(--muted)]">Status:</span> <span className={`pill pill-${order.status}`}>{order.status}</span></div>
                        <div><span className="text-[var(--muted)]">Payment:</span> {payLabel} · {order.payment_status}</div>
                        <div><span className="text-[var(--muted)]">Placed:</span> {new Date(order.created_at).toLocaleDateString()}</div>
                        <div><span className="text-[var(--muted)]">Total:</span> <span className="font-semibold">{fmtINR(order.total)}</span></div>
                    </div>
                    <div className="mb-4 border-t border-[var(--line-soft)] pt-3">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mb-1.5">Items</div>
                        {(order.items || []).map((it) => (
                            <div key={it.product_id} className="flex justify-between text-[13px] py-0.5">
                                <span>{it.name} × {it.quantity}</span>
                                <span className="text-[var(--body)]">{fmtINR((it.price || 0) * (it.quantity || 1))}</span>
                            </div>
                        ))}
                    </div>
                    {addressLine && (
                        <div className="mb-4 border-t border-[var(--line-soft)] pt-3 text-[13px]" data-testid="order-address">
                            <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mb-1">Delivering to</div>
                            <div className="text-[var(--ink)]">{addr.name}{addr.phone ? ` · ${addr.phone}` : ""}</div>
                            <div className="text-[var(--body)]">{addressLine}</div>
                        </div>
                    )}
                    {/* Shipment tracking */}
                    <div className="border-t border-[var(--line-soft)] pt-3">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mb-1.5">Shipment Tracking</div>
                        {err && <div className="text-[var(--terra)] text-sm">{err}</div>}
                        {!data && !err && <div className="text-[var(--body)] text-sm">Fetching live status…</div>}
                        {data && !data.delhivery_waybill && (
                            <div className="text-[13px] text-[var(--body)]" data-testid="not-shipped-note">
                                Your order hasn't been handed to the courier yet — tracking appears here once it ships.
                            </div>
                        )}
                        {data && data.delhivery_waybill && (
                            <>
                                <div className="text-[13px] mb-2">
                                    <div>Waybill: <span className="font-mono text-[var(--ink)]">{data.delhivery_waybill}</span> · Delhivery</div>
                                    {(latest.Status || data.delhivery_status) && (
                                        <div className="mt-1">
                                            Status: <span className="font-medium text-[var(--taupe)]">{latest.Status || data.delhivery_status}</span>
                                            {latest.StatusLocation && <> · {latest.StatusLocation}</>}
                                        </div>
                                    )}
                                    {data.tracking_error && <div className="text-[var(--terra)] text-[12px] mt-1">{data.tracking_error}</div>}
                                </div>
                                {scans.length === 0 ? (
                                    <div className="text-[13px] text-[var(--body)]">No courier scans yet. We'll update this as your parcel moves.</div>
                                ) : (
                                    <ul className="space-y-3">
                                        {scans.map((a, i) => (
                                            <li key={`${a.ScanDateTime || "scan"}-${i}`} className="border-l-2 border-[var(--taupe)] pl-3">
                                                <div className="text-[13px] text-[var(--ink)]">{a.Scan || a.Instructions}</div>
                                                <div className="text-[11px] text-[var(--body)]">
                                                    {a.ScanDateTime ? new Date(a.ScanDateTime).toLocaleString() : ""}{a.ScannedLocation ? ` · ${a.ScannedLocation}` : ""}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        )}
                    </div>

                    {/* Cancel order */}
                    {(canCancel || cancelMsg) && (
                        <div className="border-t border-[var(--line-soft)] pt-4 mt-4" data-testid="cancel-order-section">
                            {cancelMsg ? (
                                <div className="text-[13px] bg-green-700/10 text-green-800 rounded-lg px-4 py-3" data-testid="cancel-success-msg">
                                    ✓ {cancelMsg}
                                </div>
                            ) : confirming ? (
                                <div className="bg-[var(--bg-alt)]/60 rounded-lg p-4" data-testid="cancel-confirm-box">
                                    <p className="text-[13.5px] text-[var(--ink)] mb-3">
                                        Are you sure you want to cancel order <b>{order.order_number}</b>? This cannot be undone.
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            onClick={doCancel}
                                            disabled={cancelling}
                                            className="btn btn-sm text-white bg-[#a0453a] border border-[#a0453a] hover:bg-[#8b3a30] disabled:opacity-50"
                                            data-testid="confirm-cancel-order-btn"
                                        >
                                            {cancelling ? "Cancelling…" : "Yes, cancel order"}
                                        </button>
                                        <button onClick={() => setConfirming(false)} disabled={cancelling} className="btn btn-outline btn-sm" data-testid="keep-order-btn">
                                            Keep order
                                        </button>
                                    </div>
                                    {cancelErr && <div className="text-[var(--terra)] text-[12.5px] mt-2" data-testid="cancel-error-msg">{cancelErr}</div>}
                                </div>
                            ) : (
                                <button
                                    onClick={() => setConfirming(true)}
                                    className="btn btn-outline btn-sm text-[#a0453a] border-[#d8b4ab] hover:border-[#a0453a] hover:bg-[#a0453a]/5"
                                    data-testid="cancel-order-btn"
                                >
                                    Cancel Order
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
